import { createFileRoute, Link } from '@tanstack/react-router'
import { StaticNav } from '@/components/static-nav'
import { StaticFooter } from '@/components/static-footer'

export const Route = createFileRoute('/terms')({
  component: TermsRoute,
})

function Section({ id, title, summary, children }: {
  id: string
  title: string
  summary: string
  children: React.ReactNode
}) {
  return (
    <section id={id}>
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-slate-950">{title}</h2>
        <p className="mt-1 text-sm font-medium text-slate-500 italic">{summary}</p>
      </div>
      <div className="space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
    </section>
  )
}

function TermsRoute() {
  return (
    <>
      <StaticNav />
      <main className="min-h-screen bg-[#fafafa] pt-14">

        {/* Page header */}
        <div className="bg-white border-b border-slate-200 px-6 py-16">
          <div className="mx-auto max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Legal</p>
            <h1 className="text-4xl font-semibold tracking-tight text-slate-950">Terms of Service</h1>
            <p className="mt-3 text-sm text-slate-500">Last updated: April 2026</p>

            {/* Plain-language note */}
            <div className="mt-8 flex items-start gap-4 rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <span className="text-xl shrink-0 mt-0.5">📄</span>
              <div>
                <p className="text-sm font-semibold text-slate-900 mb-1">Written to be read</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Each section below includes a plain-English summary in italics. The full legal text follows. If anything feels unclear, email us at <a href="mailto:support@winboxzero.com" className="text-slate-900 underline underline-offset-2">support@winboxzero.com</a>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-16">
          <div className="mx-auto max-w-3xl space-y-16">

            <Section
              id="acceptance"
              title="1. Acceptance of terms"
              summary="By using Winbox Zero, you're agreeing to these terms. If you don't agree, don't use the service."
            >
              <p>
                By accessing or using Winbox Zero ("Service"), you agree to be bound by these Terms of Service ("Terms") and our <Link to="/privacy" className="text-slate-900 underline underline-offset-2">Privacy Policy</Link>. These Terms apply to all visitors, users, and others who access or use the Service.
              </p>
              <p>
                If you are using the Service on behalf of an organisation, you represent that you have authority to bind that organisation to these Terms, and "you" refers to both you individually and the organisation.
              </p>
            </Section>

            <Section
              id="service"
              title="2. What Winbox Zero does"
              summary="We connect to your Gmail, read metadata only, and help you organise and clean up your inbox. We never delete anything without your approval."
            >
              <p>
                Winbox Zero is an AI-powered inbox management tool. It connects to your Gmail account via Google OAuth, reads email metadata (sender, subject, timestamps, labels), and helps you identify emails that need replies, clean up subscriptions, and automate recurring inbox decisions.
              </p>
              <p>
                We do not read, store, or transmit your email body content. Every bulk action — archiving, deleting, unsubscribing — requires your explicit approval before it is executed. Gmail remains the source of truth; Winbox Zero does not maintain a separate copy of your inbox.
              </p>
              <p>
                The Service is provided as-is and may be updated, changed, or discontinued at any time. We will give reasonable notice for significant changes.
              </p>
            </Section>

            <Section
              id="account"
              title="3. Your account"
              summary="You're responsible for your account. Use a real email, keep your credentials safe, and let us know if something looks wrong."
            >
              <p>
                You must be at least 13 years old to use the Service (or the minimum age required in your jurisdiction). By connecting your Gmail account, you represent that you own that account or have permission from the account owner to use it with this Service.
              </p>
              <p>
                You are responsible for maintaining the security of your account. If you believe your account has been compromised, contact us immediately at <a href="mailto:support@winboxzero.com" className="text-slate-900 underline underline-offset-2">support@winboxzero.com</a>.
              </p>
              <p>
                One Gmail account may be connected to one Winbox Zero account. Sharing accounts across multiple users is not permitted.
              </p>
            </Section>

            <Section
              id="acceptable-use"
              title="4. Acceptable use"
              summary="Use the service to manage your own email. Don't try to abuse it, resell it, or use it in ways that harm others."
            >
              <p>You agree not to use the Service to:</p>
              <ul className="space-y-2 pl-0 list-none">
                {[
                  'Access, organise, or act on email accounts you do not own or have permission to manage',
                  'Reverse-engineer, decompile, or attempt to extract the source code of the Service',
                  'Automate actions in bulk in ways that violate Google\'s Terms of Service',
                  'Resell, sublicense, or commercially redistribute the Service without written permission',
                  'Interfere with or disrupt the integrity or performance of the Service or its infrastructure',
                  'Use the Service for any unlawful purpose or in violation of any applicable regulation',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-0.5 text-slate-400 shrink-0">—</span>
                    {item}
                  </li>
                ))}
              </ul>
              <p>
                We reserve the right to suspend or terminate accounts that violate these restrictions, with or without prior notice depending on severity.
              </p>
            </Section>

            <Section
              id="gmail"
              title="5. Google and Gmail"
              summary="We access Gmail through Google's official API. Google has its own rules — by using us, you're also agreeing to follow Google's Terms."
            >
              <p>
                The Service accesses Gmail via the Google API. By using Winbox Zero, you also agree to be bound by <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline underline-offset-2">Google's Terms of Service</a> and <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline underline-offset-2">Google's Privacy Policy</a>.
              </p>
              <p>
                The OAuth access we request is limited to the minimum necessary to provide the Service. You can revoke this access at any time via your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline underline-offset-2">Google account settings</a>. Revoking access stops all future syncing but does not automatically delete data already stored. To delete stored data, follow the account deletion process in Settings.
              </p>
            </Section>

            <Section
              id="billing"
              title="6. Billing and payments"
              summary="Paid plans start with a 30-day free trial, then bill monthly or annually unless canceled before the trial ends."
            >
              <p>
                Winbox Zero starts paid plans with a 30-day free trial. A payment method is collected before the first full cleanup pass begins, but you are not charged until the trial ends unless you cancel first. Prices are listed on the <Link to="/pricing" className="text-slate-900 underline underline-offset-2">pricing page</Link>.
              </p>
              <p>
                Payments are processed by Stripe. We do not store your payment card details. By subscribing, you authorise Stripe to charge your payment method on a recurring basis.
              </p>
              <p>
                You may cancel your subscription at any time from Settings → Billing. Cancellation takes effect at the end of the current billing period — you will not be charged again, and you retain access to paid features until the period ends. We do not offer prorated refunds for partial billing periods, except where required by applicable law.
              </p>
              <p>
                We reserve the right to change pricing with 30 days' notice to existing subscribers.
              </p>
            </Section>

            <Section
              id="privacy-ref"
              title="7. Privacy"
              summary="Your privacy matters to us. Read the full policy — it's written in plain English too."
            >
              <p>
                Your use of the Service is governed by our <Link to="/privacy" className="text-slate-900 underline underline-offset-2">Privacy Policy</Link>, which is incorporated into these Terms by reference. Please read it carefully — it explains in detail what data we collect, how we use it, and your rights over it.
              </p>
              <p>
                The short version: we read email metadata only. We never read, store, or process email body content. We don't sell your data. You can delete everything at any time.
              </p>
            </Section>

            <Section
              id="termination"
              title="8. Termination"
              summary="You can leave whenever you want. We can suspend accounts that violate these terms."
            >
              <p>
                You may stop using the Service and delete your account at any time. Deleting your account removes all associated data within 30 days.
              </p>
              <p>
                We may suspend or terminate your access if you violate these Terms, engage in behaviour that harms the Service or other users, or if we are required to do so by law. In non-urgent cases, we will attempt to notify you before taking action and give you an opportunity to resolve the issue.
              </p>
              <p>
                On termination for any reason, provisions that by their nature should survive (intellectual property, disclaimers, limitations of liability) will continue to apply.
              </p>
            </Section>

            <Section
              id="liability"
              title="9. Disclaimers and limitation of liability"
              summary="We work hard to make the service reliable, but we can't guarantee perfection. Our liability is limited."
            >
              <p>
                The Service is provided "as is" and "as available" without warranties of any kind, express or implied. We do not warrant that the Service will be uninterrupted, error-free, or free from harmful components.
              </p>
              <p>
                To the maximum extent permitted by applicable law, Winbox Zero and SU LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, the Service — including but not limited to lost data, lost revenue, or deleted emails resulting from actions you approve within the Service.
              </p>
              <p>
                Our total liability to you for any claims arising under these Terms shall not exceed the greater of (a) the amount you paid us in the 12 months prior to the claim, or (b) USD $50.
              </p>
            </Section>

            <Section
              id="changes"
              title="10. Changes to these terms"
              summary="If we change something material, we'll email you 14 days before it takes effect."
            >
              <p>
                We may update these Terms from time to time. For material changes, we will notify you by email at least 14 days before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Terms.
              </p>
              <p>
                Minor changes — clarifications, corrections, or improvements to readability that do not alter your rights or obligations — may be made without notice. The "last updated" date at the top of this page reflects the most recent revision.
              </p>
            </Section>

            <Section
              id="governing-law"
              title="11. Governing law"
              summary="These terms are governed by the laws of Wyoming, USA."
            >
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Wyoming, United States, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved in the courts of Wyoming, and you consent to the personal jurisdiction of those courts.
              </p>
            </Section>

            {/* Contact */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-base font-semibold text-slate-950 mb-1">Questions about these terms?</h2>
              <p className="text-sm text-slate-600 mb-4">
                We're a small team and we'd rather clarify something now than have you confused later. Reach out anytime.
              </p>
              <a
                href="mailto:support@winboxzero.com"
                className="inline-flex items-center text-sm font-medium text-slate-900 underline underline-offset-2"
              >
                support@winboxzero.com
              </a>
            </div>

          </div>
        </div>
      </main>
      <StaticFooter />
    </>
  )
}
